import React, { useMemo } from 'react';
import { Paper, Typography, Box, Stack, Skeleton, useTheme } from '@mui/material';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useGetReportNewCustomers } from '../../hooks/reports/useGetReportNewCustomers';
import useCurrency from '../../hooks/common/useCurrency';
import { formatNumber } from '../../utils/money';

const NewCustomersChart = ({ filter }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const theme = useTheme();
    const { locale } = useCurrency();

    const { series: rawSeries, isLoading } = useGetReportNewCustomers({
        from: filter.from,
        to: filter.to,
        granularity: filter.granularity,
    });

    const { chartData, total } = useMemo(() => {
        const chartData = rawSeries.map((p) => {
            const d = dayjs(p.date);
            const label =
                filter.granularity === 'month'
                    ? d.format('MMM YYYY')
                    : d.format('MMM D');
            return {
                label,
                count: Number(p.count ?? p.signups ?? p.value) || 0,
            };
        });
        const total = chartData.reduce((s, p) => s + p.count, 0);
        return { chartData, total };
    }, [rawSeries, filter.granularity]);

    const isEmpty = !isLoading && chartData.length === 0;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                mb: 3,
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                mb={2}
            >
                <Typography variant="h6" fontWeight={700}>
                    {isArabic ? 'العملاء الجدد' : 'New customer signups'}
                </Typography>
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        {isArabic ? 'الإجمالي' : 'Total'}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {formatNumber(total, locale)}
                    </Typography>
                </Box>
            </Stack>

            {isLoading ? (
                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
            ) : isEmpty ? (
                <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                        {isArabic ? 'لا توجد بيانات للفترة المحددة' : 'No data for the selected range'}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                            <XAxis dataKey="label" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                            <YAxis
                                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                                tickFormatter={(v) => formatNumber(v, locale)}
                                allowDecimals={false}
                                width={50}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 8,
                                }}
                                formatter={(value) => [
                                    formatNumber(value, locale),
                                    isArabic ? 'عملاء جدد' : 'New customers',
                                ]}
                            />
                            <Bar
                                dataKey="count"
                                fill={theme.palette.secondary.main}
                                radius={[4, 4, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Paper>
    );
};

export default NewCustomersChart;
